import styles from "@/asserts/styles/formSection.module.less";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import { HkdPriceLabel } from "@/components/ui/HkdPriceLabel/HkdPriceLabel";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { CURRENCY_OPTIONS, SUPPLIER_STAGE, unitOptions } from "@/constants";
import { useModalBehavior } from "@/hooks/behaviors";
import { SupplierUserSimpleListItem } from "@/types/apis";
import { Col, Form, Input, InputNumber, Row, Select, Space } from "antd";
import type { RdRawMaterialRequisitionFormValue } from "../../type";
import { useSupplierInfoFields } from "./hooks/useSupplierInfoFields";
import { useRdFormEditable } from "./hooks/useRdFormEditable";
import { useTranslation } from "react-i18next";

export const SupplierInfoFormFields = () => {
  const { t } = useTranslation("purchaseRequestManagement");
  const { isSupplierInfoFormDisabled } = useRdFormEditable();

  const form = Form.useFormInstance<RdRawMaterialRequisitionFormValue>();
  const { priceHkd, totalAmountHkd } = useSupplierInfoFields(form);

  const {
    open: supplierModalOpen,
    openModal: openSupplierModal,
    closeModal: closeSupplierModal,
  } = useModalBehavior();

  const onSelectSupplierModalClose = (
    pickedSupplier?: SupplierUserSimpleListItem,
  ) => {
    if (pickedSupplier) {
      form.setFieldsValue({
        supplierId: pickedSupplier.id,
        supplierCode: pickedSupplier.supplierCode,
        supplierName:
          pickedSupplier.supplierName || pickedSupplier.supplierUsName,
      });
    }
    closeSupplierModal();
  };

  return (
    <div className={styles.formSection}>
      <ModalSectionHeader title={t("供应商信息")} />

      <div className={styles.sectionBody}>
        <Form.Item<RdRawMaterialRequisitionFormValue>
          name="supplierFormId"
          hidden
        />

        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<RdRawMaterialRequisitionFormValue>
              name="supplierId"
              hidden
            ></Form.Item>

            <Form.Item<RdRawMaterialRequisitionFormValue>
              name="supplierCode"
              label={t("供应商编号")}
              rules={[
                {
                  required: true,
                  message: t("请选择供应商"),
                },
              ]}
            >
              <Input
                placeholder={t("请选择供应商")}
                readOnly
                onClick={openSupplierModal}
                disabled={isSupplierInfoFormDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<RdRawMaterialRequisitionFormValue>
              name="supplierName"
              label={t("供应商名称")}
            >
              <Input
                placeholder={t("请选择供应商")}
                readOnly
                onClick={openSupplierModal}
                disabled={isSupplierInfoFormDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <MaterialFileFormFields
              fieldName="quotationFiles"
              label={t("报价单")}
              required={false}
              isCustomDisabled
              disabled={isSupplierInfoFormDisabled}
              hiddenRemove={isSupplierInfoFormDisabled}
            />
          </Col>

          <Col span={8}>
            <Form.Item label={t("数量/单位")} required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="quantity"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请输入"),
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "60%" }}
                    placeholder={t("请输入")}
                    min={1}
                    precision={0}
                    disabled={isSupplierInfoFormDisabled}
                  />
                </Form.Item>
                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="quantityUnit"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请选择"),
                    },
                  ]}
                >
                  <Select
                    style={{ width: "40%" }}
                    options={unitOptions}
                    placeholder={t("请选择")}
                    disabled={isSupplierInfoFormDisabled}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label={t("单价/币种")} required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="price"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请输入单价"),
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "60%" }}
                    placeholder={t("请输入")}
                    min={0}
                    precision={2}
                    disabled={isSupplierInfoFormDisabled}
                  />
                </Form.Item>
                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="priceUnit"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请选择币种"),
                    },
                  ]}
                >
                  <Select
                    style={{ width: "40%" }}
                    options={CURRENCY_OPTIONS}
                    placeholder={t("请选择")}
                    onChange={(value) => {
                      form.setFieldsValue({ totalAmountUnit: value });
                    }}
                    disabled={isSupplierInfoFormDisabled}
                  />
                </Form.Item>

                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="priceHkd"
                  noStyle
                  hidden
                >
                  <Input />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <HkdPriceLabel priceHkd={priceHkd} />
          </Col>

          <Col span={8}>
            <Form.Item label={t("总价/币种")} required>
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="totalAmount"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请输入总价"),
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "60%" }}
                    placeholder={t("请输入")}
                    min={0}
                    precision={2}
                    disabled={isSupplierInfoFormDisabled}
                  />
                </Form.Item>
                <Form.Item<RdRawMaterialRequisitionFormValue>
                  name="totalAmountUnit"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: t("请选择币种"),
                    },
                  ]}
                >
                  <Select
                    style={{ width: "40%" }}
                    options={CURRENCY_OPTIONS}
                    placeholder={t("请选择")}
                    disabled
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <HkdPriceLabel priceHkd={totalAmountHkd} />
          </Col>

          <Col span={24}>
            <Form.Item<RdRawMaterialRequisitionFormValue>
              name="remarks"
              label={t("备注")}
            >
              <Input.TextArea
                rows={2}
                placeholder={t("请输入")}
                disabled={isSupplierInfoFormDisabled}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <Form disabled={false}>
        <SelectSupplierModal
          open={supplierModalOpen}
          onClose={onSelectSupplierModalClose}
          fixedSupplierStage={SUPPLIER_STAGE.ONBOARDING}
        />
      </Form>
    </div>
  );
};
